import { REGION } from '@env';
import { format } from 'date-fns';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth/services';

import { FlockLprService } from '../../integrations';
import { ViLPRProvider } from '../../shared/models';
import { getBoundingBox, QueriesSearchParams } from '../../utils/utils';
import { LPR_ERRORS, LprWarningEntry } from '../constants/errors';
import { IFsHistoricalProviderResultsInterface } from '../interfaces';
import { IFsLprParameters } from '../interfaces/common/parameters';
import { IFsLprProviderInterface } from '../interfaces/common/provider';
import { IFsLprResult } from '../interfaces/common/result';
import { IFsLprFlockParameters } from '../interfaces/lpr-flock-parameters.interface';
import { FlockLprResults } from '../interfaces/lpr-flock-results';
import { LprReadsFieldsModel } from '../models';

@Injectable({ providedIn: 'root' })
export class FlockProviderService implements IFsLprProviderInterface {
  private static readonly defaultRadiusInMiles: number = 25;
  private static readonly metersPerMile = 1609.344;
  private static readonly dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX";
  private static readonly maxPageSize = 400;

  public name = 'Flock';
  public nameEnum?: ViLPRProvider;

  public get loaded$(): Observable<boolean> {
    return this._loaded$;
  }

  public get historicLoaded$(): Observable<boolean> {
    return this._historicLoaded$;
  }

  get region(): string {
    return REGION;
  }

  // Error array to push any errors to be displayed.
  public errors: string[] = [];

  // New warnings array for non-critical filter limitations
  public warnings: LprWarningEntry[] = [];

  public isLoading: boolean;
  public isInit: boolean;
  public isFinished: boolean = false;
  public fields: LprReadsFieldsModel;

  private _params: IFsLprFlockParameters;
  private _loaded$: Subject<boolean> = new Subject<boolean>();
  private _historicLoaded$: Subject<boolean> = new Subject<boolean>();
  private _inited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _flockNextPage: string = null;
  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _cancel$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _flockLprService: FlockLprService,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    if (this._sdmEnabled) {
      this.nameEnum = ViLPRProvider.FLOCK;
      this.isLoading = true;
      this.isInit = false;
      this.total = 0;
    }
  }

  public results: IFsLprResult[] = [];
  public total: number = 0;

  /**
   * Initialize the provider
   */
  public init(): void {
    this.isInit = true;
    this._inited$.next(true);
  }

  public search(query: Partial<IFsLprParameters>): void {
    const inited$ = this._sdmEnabled
      ? this._inited$.pipe(
          filter((inited) => inited),
          take(1),
        )
      : this._inited$;
    inited$.subscribe((inited) => {
      if (inited) {
        this._startSearch(query);
      }
    });
  }

  /**
   * Used for pagination and to check if there're next pages.
   * @returns Does not return anything.
   */
  public next(): void {
    if (this.isFinished || this.isLoading) {
      return;
    }
    // If there are errors, we don't want to load the next page
    if (this.errors.length > 0) {
      this.isLoading = false;
      return;
    }
    if (this._flockNextPage !== null) {
      // Flock API requires either the page or the plate to be set, but not both.
      // We send the plate during the first search and then send only the page
      // (since the plate is preserved by Flock API and we get the page id in the first response)
      // during the pagination. We set the plate to null when we're getting the next page.
      this._params = { page: this._flockNextPage };
    } else if (this._flockNextPage === null) {
      this.isFinished = true;
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this._flockLprService
      .getFlockPlatesV2(this._params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_NEXT_PAGE);
          this.isLoading = false;
          this.isFinished = true;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  public getHistoricalResults(
    searchParams: Partial<IFsLprParameters>,
  ): Observable<IFsHistoricalProviderResultsInterface> {
    const modifiedParams = this._historicalSearchTransformParams(searchParams);

    return this._flockLprService.getFlockPlatesV2(modifiedParams).pipe(
      map((response) => {
        return {
          results: response.results || [],
          total: response.results.length || 0,
        };
      }),
      catchError(() => {
        return of(null);
      }),
      takeUntil(this._destroy$),
    );
  }

  public cancel(): void {
    this._cancel$.next();
  }

  private get _sdmEnabled(): boolean {
    return this._currentOrganization?.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  private _startSearch(query: Partial<IFsLprParameters>): void {
    this.cancel(); // Cancel any ongoing search
    this.errors = [];
    this.warnings = [];
    this.results = [];
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }

    const params = this._searchTransformParams(query);
    if (this.errors.length > 0) {
      this.isLoading = false;
      this.results = []; // reset results to avoid showing old results after errors
      this.total = 0;
      this._loaded$.next(true);
      return;
    }
    this._flockLprService
      .getFlockPlatesV2(params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_SEARCH_RESULTS);
          this.isLoading = false;
          this.total = 0;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  private _handleResponse(response: FlockLprResults): void {
    this._flockNextPage = null;
    if (!response) {
      this.isLoading = false;
      this._loaded$.next(true);
      this.isFinished = true;
      return;
    }
    if (response) {
      if (response.links.next) {
        const nextPageArray = response.links.next.split('=');
        if (nextPageArray.length === 2) {
          this._flockNextPage = nextPageArray[1];
        }
      }
      const items = response.results || [];
      this.total = response.resultCount || 0;
      this.results = [...this.results, ...items];
      this.isLoading = false;

      this.isFinished =
        this.results.length >= this.total || !this._flockNextPage || items.length === 0;

      this._loaded$.next(true);
    }
  }

  private _baseTransformParams(params: Partial<IFsLprParameters>): Partial<IFsLprFlockParameters> {
    const {
      fromDate: _fromDate,
      toDate: _toDate,
      plate: _plate,
      pageSize: _pageSize,
      sort,
      // limit, // TODO: FWX-1888 - Standardize/debug search pagination across providers
    } = params;

    const [fromDate, toDate] = this._getFormattedDates(_fromDate?.toString(), _toDate?.toString());
    const plate = this._getPlate(_plate);

    const pageSize =
      _pageSize && _pageSize <= FlockProviderService.maxPageSize
        ? _pageSize
        : FlockProviderService.maxPageSize;

    return {
      plate,
      fromDate,
      toDate,
      pageSize,
      sortOrder: sort,
      // maxResults: limit, // TODO: FWX-1888 - Standardize/debug search pagination across providers
    };
  }

  private _searchTransformParams(params: Partial<IFsLprParameters>): IFsLprFlockParameters {
    const copyOfParams = { ...params };
    // Flock API does not support plate state
    if (copyOfParams.plateState) {
      this.warnings.push({
        type: 'PLATE_STATE_NOT_SUPPORTED',
        message: LPR_ERRORS.PLATE_STATE_NOT_SUPPORTED,
      });
      delete copyOfParams.plateState;
    }

    // Flock API does not support vehicle color
    if (copyOfParams.vehicleColor?.length) {
      this.warnings.push({
        type: 'VEHICLE_COLOR_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_COLOR_NOT_SUPPORTED,
      });
      delete copyOfParams.vehicleColor;
    }

    // Flock API does not support vehicle make
    if (copyOfParams.vehicleMake) {
      this.warnings.push({
        type: 'VEHICLE_MAKE_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_MAKE_NOT_SUPPORTED,
      });
      delete copyOfParams.vehicleMake;
    }

    // Flock API does not support vehicle body
    if (copyOfParams.vehicleBody) {
      this.warnings.push({
        type: 'VEHICLE_BODY_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_BODY_NOT_SUPPORTED,
      });
      delete copyOfParams.vehicleBody;
    }

    if (copyOfParams.assignedUserIds?.length) {
      this.warnings.push({
        type: 'ASSIGNED_USER_IDS_NOT_SUPPORTED',
        message: LPR_ERRORS.ASSIGNED_USER_IDS_NOT_SUPPORTED,
      });
      delete copyOfParams.assignedUserIds;
    }

    if (copyOfParams.deviceNames?.length) {
      this.warnings.push({
        type: 'DEVICE_NAMES_NOT_SUPPORTED',
        message: LPR_ERRORS.DEVICE_NAMES_NOT_SUPPORTED,
      });
      delete copyOfParams.deviceNames;
    }

    const inBbox = this._getBoundingBox(copyOfParams.inBbox);

    this._params = {
      ...this._baseTransformParams(copyOfParams),
      inBbox,
    };

    return this._params;
  }

  private _historicalSearchTransformParams(
    params: Partial<IFsLprParameters>,
  ): IFsLprFlockParameters {
    return this._baseTransformParams(params);
  }

  private _getFormattedDates(fromDate, toDate): string[] {
    if (this.region !== 'US') {
      fromDate = fromDate
        ? format(new Date(fromDate), FlockProviderService.dateFormat).replace('+', '-')
        : null;
      toDate = toDate
        ? format(new Date(toDate), FlockProviderService.dateFormat).replace('+', '-')
        : null;
    } else {
      fromDate = fromDate ? format(new Date(fromDate), FlockProviderService.dateFormat) : null;
      toDate = toDate ? format(new Date(toDate), FlockProviderService.dateFormat) : null;
    }
    return [fromDate, toDate];
  }

  private _getPlate(plate: string) {
    return plate == '' || plate == null ? '*' : plate;
  }

  private _getBoundingBox(_inBbox: QueriesSearchParams): string {
    // We set these two to default if we don't have a bounding box.
    const radius = Math.round(
      FlockProviderService.defaultRadiusInMiles * FlockProviderService.metersPerMile,
    );
    const point = this._currentOrganization.settings.geometry.coordinates.join();

    let inBbox;
    if (point && radius && !_inBbox) {
      const points = point.split(',');
      if (points.length !== 2) {
        this.errors.push(LPR_ERRORS.INVALID_COORDINATES);
        return;
      }
      const cir = new google.maps.Circle({
        center: new google.maps.LatLng({
          lat: +points[1],
          lng: +points[0],
        }),
        radius,
      });
      const bounds = cir.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      inBbox = `${sw.lng()},${ne.lat()},${ne.lng()},${sw.lat()}`;
    } else if (_inBbox) {
      inBbox = getBoundingBox(
        _inBbox,
        this._sdmEnabled && this.nameEnum ? this.nameEnum : this.name,
      );
    }

    return inBbox;
  }
}
